import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Header from "../components/Header";
import { resources, typeIcons, categories } from "../data/resourcesData";
import "../styles/global.css";
import "../styles/resource-detail.css";
import { Helmet } from "react-helmet";
const ResourceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoaded, setIsLoaded] = useState(false);
  const [resource, setResource] = useState(null);
  const [relatedResources, setRelatedResources] = useState([]);
  const [activeTab, setActiveTab] = useState("content");
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [emailInput, setEmailInput] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${id}`;
    // Find the current resource
    const currentResource = resources.find((r) => r.id === id);
    setResource(currentResource);

    if (currentResource) {
      // Find related resources in the same category
      const related = resources
        .filter((r) => r.id !== id && r.category === currentResource.category)
        .slice(0, 3);
      setRelatedResources(related);
    }

    // Delay setting isLoaded to trigger animations
    setTimeout(() => {
      setIsLoaded(true);
    }, 100);

    // Initialize AdSense
    try {
      if (window.adsbygoogle && process.env.NODE_ENV !== "development") {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      }
    } catch (e) {
      console.error("AdSense error:", e);
    }
  }, [id]);

  const handlePrintResource = () => {
    window.print();
  };

  const handleShareResource = () => {
    setShowShareMenu(!showShareMenu);
  };

  const handleShareVia = (platform) => {
    const url = window.location.href;
    const title = resource?.title;

    switch (platform) {
      case "facebook":
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            url
          )}`,
          "_blank"
        );
        break;
      case "twitter":
        window.open(
          `https://twitter.com/intent/tweet?url=${encodeURIComponent(
            url
          )}&text=${encodeURIComponent(title)}`,
          "_blank"
        );
        break;
      case "email":
        window.open(
          `mailto:?subject=${encodeURIComponent(
            title
          )}&body=${encodeURIComponent(`Check out this resource: ${url}`)}`,
          "_blank"
        );
        break;
      default:
        if (navigator.share) {
          navigator.share({
            title: title,
            url: url,
          });
        }
    }

    setShowShareMenu(false);
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (emailInput && emailInput.includes("@")) {
      setShowThankYou(true);
      setEmailInput("");

      // Hide thank you message after 3 seconds
      setTimeout(() => {
        setShowThankYou(false);
      }, 3000);
    }
  };

  if (!resource) {
    return (
      <div className="resource-detail-container">
        <Header />
        <Helmet>
          <title>Resources detail page</title>
          <meta
            name="description"
            content="Resources detail page that contains information about a particle resource"
          />
        </Helmet>
        <div className="not-found-container">
          <motion.div
            className="not-found-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}>
            <div className="not-found-icon">
              <i className="fas fa-file-search"></i>
            </div>
            <h2>Resource Not Found</h2>
            <p>
              We couldn't find the resource you're looking for. It may have been
              moved or deleted.
            </p>
            <button
              onClick={() => navigate("/resources")}
              className="primary-button">
              <i className="fas fa-arrow-left"></i>
              <span>Back to Resources</span>
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  const categoryName = categories.find((c) => c.id === resource.category)?.name;

  return (
    <div className="resource-detail-container">
      <Header />

      <section className="resource-hero">
        <div className="container">
          <motion.div
            className="resource-hero-content"
            initial={{ opacity: 0, y: 20 }}
            animate={isLoaded ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}>
            <div className="resource-breadcrumb">
              <Link to="/resources">Resources</Link>
              <span className="breadcrumb-separator">
                <i className="fas fa-chevron-right"></i>
              </span>
              <Link to={`/resources?category=${resource.category}`}>
                {categoryName}
              </Link>
              <span className="breadcrumb-separator">
                <i className="fas fa-chevron-right"></i>
              </span>
              <span className="current-page">{resource.title}</span>
            </div>

            <h1>{resource.title}</h1>

            <div className="resource-meta">
              <div className="resource-tags">
                <span className="type-badge">
                  <i className={`fas ${typeIcons[resource.type]}`}></i>
                  <span>{resource.type}</span>
                </span>
                <span className="category-badge">
                  <i className="fas fa-folder"></i>
                  <span>{categoryName}</span>
                </span>
              </div>

              <div className="resource-actions">
                <button
                  className="action-button print-button"
                  onClick={handlePrintResource}
                  aria-label="Print resource">
                  <i className="fas fa-print"></i>
                </button>

                <div className="share-container">
                  <button
                    className="action-button share-button"
                    onClick={handleShareResource}
                    aria-label="Share resource">
                    <i className="fas fa-share-alt"></i>
                  </button>

                  <AnimatePresence>
                    {showShareMenu && (
                      <motion.div
                        className="share-menu"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.2 }}>
                        <button onClick={() => handleShareVia("facebook")}>
                          <i className="fab fa-facebook-f"></i>
                          <span>Facebook</span>
                        </button>
                        <button onClick={() => handleShareVia("twitter")}>
                          <i className="fab fa-twitter"></i>
                          <span>Twitter</span>
                        </button>
                        <button onClick={() => handleShareVia("email")}>
                          <i className="fas fa-envelope"></i>
                          <span>Email</span>
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>

            <p className="lead-text">{resource.description}</p>
          </motion.div>

          <motion.div
            className="resource-hero-image"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isLoaded ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}>
            <img src={resource.image} alt={resource.title} />
          </motion.div>
        </div>
      </section>

      <div className="container">
        {/* Top Ad Placement */}
        <div
          className="ad-container resource-top-ad"
          style={{ width: "100%", minWidth: "300px" }}>
          {/* <div className="ad-label">Advertisement</div> */}
          <ins
            className="adsbygoogle"
            style={{ display: "block" }}
            data-ad-client="ca-pub-2067522743161930"
            data-ad-slot="1234567890"
            data-ad-format="auto"
            data-full-width-responsive="true"></ins>
        </div>
      </div>

      <section className="resource-main">
        <div className="container">
          <div className="resource-content-wrapper">
            <div className="resource-tabs">
              <button
                className={`tab-button ${
                  activeTab === "content" ? "active" : ""
                }`}
                onClick={() => setActiveTab("content")}>
                <i className="fas fa-book-open"></i>
                <span>Content</span>
              </button>
              <button
                className={`tab-button ${
                  activeTab === "download" ? "active" : ""
                }`}
                onClick={() => setActiveTab("download")}>
                <i className="fas fa-download"></i>
                <span>Download</span>
              </button>
              <button
                className={`tab-button ${
                  activeTab === "related" ? "active" : ""
                }`}
                onClick={() => setActiveTab("related")}>
                <i className="fas fa-link"></i>
                <span>Related</span>
              </button>
            </div>

            <div className="resource-tab-content">
              <AnimatePresence mode="wait">
                {activeTab === "content" && (
                  <motion.div
                    key="content"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className="resource-content">
                    {resource.content.map((section, idx) => (
                      <motion.div
                        key={idx}
                        className="content-section"
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: idx * 0.1 }}>
                        <h2>{section.heading}</h2>
                        {section.paragraphs.map((para, pidx) => (
                          <p
                            key={pidx}
                            dangerouslySetInnerHTML={{
                              __html: para.replace(/\n/g, "<br>"),
                            }}></p>
                        ))}

                        {/* Mid-page Ad after the second section */}
                        {idx === 1 && (
                          <div
                            className="ad-container resource-mid-ad"
                            style={{ width: "100%", minWidth: "300px" }}>
                            {/* <div className="ad-label">Advertisement</div> */}
                            <ins
                              className="adsbygoogle"
                              style={{ display: "block" }}
                              data-ad-client="ca-pub-2067522743161930"
                              data-ad-slot="1234567891"
                              data-ad-format="auto"
                              data-full-width-responsive="true"></ins>
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </motion.div>
                )}

                {activeTab === "download" && (
                  <motion.div
                    key="download"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className="download-content">
                    <div className="download-card">
                      <div className="download-icon">
                        <i className="fas fa-file-pdf"></i>
                      </div>
                      <div className="download-info">
                        <h3>{resource.title} (PDF)</h3>
                        <p>
                          Download this resource as a printable PDF to reference
                          offline.
                        </p>
                        <button className="download-button">
                          <i className="fas fa-download"></i>
                          <span>Download PDF</span>
                        </button>
                      </div>
                    </div>

                    <div className="email-resource-form">
                      <h3>Email this resource to yourself</h3>
                      <p>We'll send a copy to your inbox for easy reference.</p>

                      <form onSubmit={handleEmailSubmit}>
                        <div className="form-group">
                          <input
                            type="email"
                            placeholder="Your email address"
                            value={emailInput}
                            onChange={(e) => setEmailInput(e.target.value)}
                            required
                          />
                          <button type="submit">
                            <i className="fas fa-paper-plane"></i>
                            <span>Send</span>
                          </button>
                        </div>

                        <AnimatePresence>
                          {showThankYou && (
                            <motion.div
                              className="thank-you-message"
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}>
                              <i className="fas fa-check-circle"></i>
                              <p>
                                Thank you! The resource has been sent to your
                                email.
                              </p>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </form>
                    </div>
                  </motion.div>
                )}

                {activeTab === "related" && (
                  <motion.div
                    key="related"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className="related-resources">
                    <h3>Similar Resources</h3>
                    <div className="related-grid">
                      {relatedResources.length > 0 ? (
                        relatedResources.map((relatedResource, index) => (
                          <Link
                            key={relatedResource.id}
                            to={`/resources/${relatedResource.id}`}
                            className="related-card">
                            <div className="related-image">
                              <img
                                src={relatedResource.image}
                                alt={relatedResource.title}
                              />
                              <div className="type-tag">
                                <i
                                  className={`fas ${
                                    typeIcons[relatedResource.type]
                                  }`}></i>
                                <span>{relatedResource.type}</span>
                              </div>
                            </div>
                            <div className="related-content">
                              <h4>{relatedResource.title}</h4>
                              <p>{relatedResource.description}</p>
                            </div>
                          </Link>
                        ))
                      ) : (
                        <div className="no-related">
                          <p>No related resources found for this category.</p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="resource-sidebar">
              <div className="resource-sticky-sidebar">
                <div className="sidebar-card table-of-contents">
                  <h3>Table of Contents</h3>
                  <ul className="toc-list">
                    {resource.content.map((section, idx) => (
                      <li key={idx}>
                        <a href={`#section-${idx}`} className="toc-link">
                          {section.heading}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="sidebar-card assessment-cta">
                  <h3>Relationship Assessment</h3>
                  <p>
                    Gain clarity about your relationship with our research-based
                    assessment.
                  </p>
                  <Link to="/quiz" className="sidebar-button">
                    <span>Take Free Assessment</span>
                    <i className="fas fa-arrow-right"></i>
                  </Link>
                </div>

                {/* Sidebar Ad Placement */}
                <div
                  className="ad-container sidebar-ad"
                  style={{ width: "100%", minWidth: "300px" }}>
                  {/* <div className="ad-label">Advertisement</div> */}
                  <ins
                    className="adsbygoogle"
                    style={{ display: "block" }}
                    data-ad-client="ca-pub-2067522743161930"
                    data-ad-slot="1234567892"
                    data-ad-format="auto"
                    data-full-width-responsive="true"></ins>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="resource-actions-bar">
        <div className="container">
          <div className="actions-container">
            <Link to="/resources" className="back-button">
              <i className="fas fa-arrow-left"></i>
              <span>Back to Resources</span>
            </Link>

            <div className="resource-navigation">
              {resource.prevResource && (
                <Link
                  to={`/resources/${resource.prevResource.id}`}
                  className="nav-link prev">
                  <i className="fas fa-chevron-left"></i>
                  <span>Previous Resource</span>
                </Link>
              )}

              {resource.nextResource && (
                <Link
                  to={`/resources/${resource.nextResource.id}`}
                  className="nav-link next">
                  <span>Next Resource</span>
                  <i className="fas fa-chevron-right"></i>
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="container">
        {/* Bottom Ad Placement */}
        <div
          className="ad-container resource-bottom-ad"
          style={{ width: "100%", minWidth: "300px" }}>
          {/* <div className="ad-label">Advertisement</div> */}
          <ins
            className="adsbygoogle"
            style={{ display: "block" }}
            data-ad-client="ca-pub-2067522743161930"
            data-ad-slot="1234567893"
            data-ad-format="auto"
            data-full-width-responsive="true"></ins>
        </div>
      </div>

      <section className="more-resources">
        <div className="container">
          <h2 className="section-title">Explore More Resources</h2>
          <div className="categories-grid">
            {categories
              .filter((c) => c.id !== "all")
              .map((category, index) => (
                <Link
                  key={category.id}
                  to={`/resources?category=${category.id}`}
                  className="category-card">
                  <div className="category-icon">
                    <i className={getCategoryIcon(category.id)}></i>
                  </div>
                  <h3>{category.name}</h3>
                  <p>{getCategoryDescription(category.id)}</p>
                  <span className="explore-link">
                    Explore Resources
                    <i className="fas fa-arrow-right"></i>
                  </span>
                </Link>
              ))}
          </div>
        </div>
      </section>

      <footer className="site-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-logo">
              <h3 style={{ color: "#FFF" }}>Relationship Wellness</h3>
              <p style={{ color: "#FFF" }}>
                Evidence-based insights for healthier relationships.
              </p>
              <div className="social-icons">
                <a href="#" aria-label="Facebook">
                  <i className="fab fa-facebook-f"></i>
                </a>
                <a href="#" aria-label="Instagram">
                  <i className="fab fa-instagram"></i>
                </a>
                <a href="#" aria-label="Twitter">
                  <i className="fab fa-twitter"></i>
                </a>
                <a href="#" aria-label="LinkedIn">
                  <i className="fab fa-linkedin-in"></i>
                </a>
              </div>
            </div>
            <div className="footer-links">
              <h4 style={{ color: "#FFF" }}>Quick Links</h4>
              <ul>
                <li>
                  <a href="/">Home</a>
                </li>
                <li>
                  <a href="/about">About Us</a>
                </li>
                <li>
                  <a href="/blog">Blog</a>
                </li>
                <li>
                  <a href="/resources">Resources</a>
                </li>
                <li>
                  <a href="/contact">Contact</a>
                </li>
              </ul>
            </div>
            <div className="footer-contact">
              <h4 style={{ color: "#FFF" }}>Contact Us</h4>
              <p>
                <i className="fas fa-envelope"></i> support@nivexa.net
              </p>
              <p>
                <i className="fas fa-phone"></i> (555) 123-4567
              </p>
              <p>
                <i className="fas fa-map-marker-alt"></i> 123 Wellness Ave,
                Suite 200
                <br />
                San Francisco, CA 94110
              </p>
            </div>
          </div>
          <div className="footer-bottom">
            <p className="copyright">
              © {new Date().getFullYear()} Relationship Wellness. All rights
              reserved.
            </p>
            <div className="legal-links">
              <a href="/privacy">Privacy Policy</a>
              <a href="/terms">Terms of Use</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Helper functions
function getCategoryIcon(categoryId) {
  const icons = {
    communication: "fa-comments",
    conflict: "fa-balance-scale",
    growth: "fa-seedling",
    transitions: "fa-door-open",
  };
  return icons[categoryId] || "fa-folder";
}

function getCategoryDescription(categoryId) {
  const descriptions = {
    communication:
      "Learn techniques to express yourself and listen effectively.",
    conflict: "Transform disagreements into opportunities for growth.",
    growth: "Develop personal strength and emotional intelligence.",
    transitions: "Navigate relationship changes with resilience and clarity.",
  };
  return descriptions[categoryId] || "";
}

export default ResourceDetail;
